/**
 * 发票信息
 */
import React, { memo, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Select, Button, Input } from 'antd';
import intl from 'react-intl-universal';
import { renderContent } from '@/utils';
import { parseYuan2Currency, parseFen2Currency } from '@/utils/number';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import {
  alterInvoiceItemData,
  inputInvoiceInfos,
  updateRemark,
} from '@/redux/actions/bill/invoice';
import { ReducerState, storeName } from '@/redux/reducers/bill/invoice';
import InputWithCoupon from '@/components/Reservation/NumberInput/InputWithCoupon';
import RemarkInfo from '../RemarkInfo';
import * as helper from '../../helper';
// import { invoiceTypeOptions, invoiceTypeMap } from '../../constant';
import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { invoices, remark } = state;
  return { invoices, remark };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  invoiceItemChange: alterInvoiceItemData,
  invoiceInfoChange: inputInvoiceInfos,
  remarkChange: updateRemark,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IProps {
  isView: boolean; // 预览
}

const Index: React.FC<IProps & MapStateProps & MapDispatchProps> = (props) => {
  const { isView, invoices, remark, invoiceItemChange, invoiceInfoChange, remarkChange } = props;

  const [invoiceTypeOptions, setInvoiceTypeOptions] = useState([]);
  const [invoiceTypeMap, setInvoiceTypeMap] = useState({});

  // 获取发票类型
  const getInvoicesTypeList = async () => {
    const { options, optionsMap } = await helper.getInvoicesTypeList();
    setInvoiceTypeOptions(options);
    setInvoiceTypeMap(optionsMap);
  };

  useEffect(() => {
    getInvoicesTypeList();
  }, []);

  const renderInvoiceInfosItem = () => {
    if (!invoices || invoices.length === 0) return null;
    if (isView)
      return invoices.map((record, i) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className={styles.row}>
            <div className={styles.cell}>{invoiceTypeMap[record?.invoiceType] || '-'}</div>
            <div className={styles.cell}>{record?.invoiceNo || '-'}</div>
            <div className={styles.cell}>{record?.invoiceCode || ''}</div>
            <div className={styles.cell}>{parseFen2Currency(record.amount)}</div>
          </div>
        );
      });
    return invoices.map((record, i) => {
      const hasRemoveBtn = i !== 0;
      return (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className={styles.row}>
          <div className={styles.cell}>
            <Select
              value={record?.invoiceType}
              style={{ width: 240 }}
              placeholder={intl.get('bill_invoice_modal_please_select')}
              options={invoiceTypeOptions}
              onChange={(val) => invoiceInfoChange('invoiceType', record.id, val)}
            />
          </div>
          <div className={styles.cell}>
            <Input
              value={record?.invoiceNo}
              style={{ width: 240 }}
              placeholder={intl.get('bill_invoice_modal_input_invoice_number')}
              maxLength={30}
              onChange={(e) => invoiceInfoChange('invoiceNo', record.id, e.target.value)}
            />
          </div>
          <div className={styles.cell}>
            <Input
              value={record?.invoiceCode}
              style={{ width: 240 }}
              placeholder={intl.get('bill_invoice_modal_input_invoice_code')}
              maxLength={30}
              onChange={(e) => invoiceInfoChange('invoiceCode', record.id, e.target.value)}
            />
          </div>
          <div className={styles.cell}>
            <InputWithCoupon
              style={{ width: 240 }}
              withCoupon={false}
              value={record?.amount}
              onChange={(val) => invoiceInfoChange('amount', record.id, val)}
            />
          </div>
          {hasRemoveBtn ? (
            <div>
              <Button type="link" danger onClick={() => invoiceItemChange('remove', record.id)}>
                {intl.get('bill_invoice_modal_remove')}
              </Button>
            </div>
          ) : null}
        </div>
      );
    });
  };

  const renderTotalPrice = () => {
    let total = null;
    (invoices || []).forEach((item) => {
      if (item.amount !== null && item.amount !== undefined) {
        total += Number(item.amount || 0);
      }
    });
    if (isView) return parseFen2Currency(total);
    return renderContent(parseYuan2Currency(total));
  };

  const renderRemark = () => {
    if (isView)
      return (
        <>
          <div className={styles.headerInfos}>{intl.get('bill_invoice_modal_remark_colon')}</div>
          <div>{remark}</div>
        </>
      );
    return (
      <RemarkInfo
        value={remark}
        title={intl.get('bill_invoice_modal_remark')}
        placeholder={intl.get('bill_invoice_modal_remark_info')}
        disabled={isView}
        onChange={(val) => remarkChange(val)}
      />
    );
  };

  return (
    <div className={styles.InvoiceInfos}>
      <div className={styles.headerInfos}>
        {intl.get('bill_invoice_modal_invoice_info_colon')}
        {isView ? null : (
          <Button className={styles.addBtn} onClick={() => invoiceItemChange('add')}>
            {intl.get('bill_invoice_modal_add')}
          </Button>
        )}
      </div>
      <div className={styles.table}>
        <div className={styles.header}>
          <div className={styles.row}>
            <div className={styles.cell}>{intl.get('bill_invoice_modal_invoice_type')}</div>
            <div className={styles.cell}>{intl.get('bill_invoice_modal_invoice_no')}</div>
            <div className={styles.cell}>{intl.get('bill_invoice_modal_invoice_code')}</div>
            <div className={styles.cell}>{intl.get('bill_invoice_modal_invoice_amount')}</div>
          </div>
        </div>
        <div className={styles.body}>
          {renderInvoiceInfosItem()}
          {isView ? (
            <div className={styles.totalPrice}>
              <span className={styles.infoColor}>{intl.get('bill_invoice_modal_total_colon')}</span>
              {renderTotalPrice()}
            </div>
          ) : (
            <div className={styles.row}>
              <div className={`${styles.cell} ${styles.noDecoration}`} />
              <div className={`${styles.cell} ${styles.noDecoration}`} />
              <div className={`${styles.cell} ${styles.noDecoration}`} />
              <div className={`${styles.cell} ${styles.noDecoration}`}>
                <span className={styles.infoColor}>
                  {intl.get('bill_invoice_modal_total_colon')}
                </span>
                {renderTotalPrice()}
              </div>
            </div>
          )}
        </div>
      </div>
      {renderRemark()}
    </div>
  );
};

export default connect<MapStateProps, MapDispatchProps, IProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
